import type { IButtonProps } from './Button'

import { styled } from '@linaria/react'
import { IconSC } from 'atoms/Icon'
import { TextBoxSC } from 'atoms/TextBox'
import { BORDERS, CORNERS, FIXED_INDENTS, MEDIA, TRANSITIONS } from 'vars/index'
import {
  ButtonBordersWidth,
  ButtonCorners,
  ButtonFluid,
  ButtonLoaderSizes,
  loaderFootprintMap,
} from './Button.h'

export const ButtonOverlaySC = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  transition: background-color var(${TRANSITIONS.LINEAR_200});
`

export const ButtonSC = styled.button<Omit<IButtonProps, 'content'>>`
  &[data-arcana-button='true'] {
    position: relative;
    display: inline-flex;
    box-sizing: border-box;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    max-width: 100%;
    border: none;
    min-width: ${({ icon, title }) => (title ? (icon ? 164 : 132) : 0)}px;
    border-radius: ${({ corners }) =>
      `var(${CORNERS[corners || ButtonCorners.CORNERS_0]})`};
    box-shadow: ${({ borderWidth, bordersColorDefault }) =>
      `inset 0 0 0 var(${
        BORDERS[borderWidth || ButtonBordersWidth.BORDER_0]
      }) var(${bordersColorDefault}) `};
    outline: none;
    cursor: pointer;
    overflow: hidden;
    transition: background-color var(${TRANSITIONS.LINEAR_200});
    font: ${({ font }) => `var(${font})`};
    height: ${({ fluid }) =>
      fluid === ButtonFluid.Both || fluid === ButtonFluid.Vertical
        ? '100%'
        : 'auto'};
    width: ${({ fluid }) =>
      fluid === ButtonFluid.Horizontal || fluid === ButtonFluid.Both
        ? '100%'
        : 'auto'};

    padding: ${({ paddings }) =>
      paddings?.length
        ? paddings.map((padding) => `var(${padding})`).join(' ')
        : 0};

    background: ${({ backgroundColorDefault }) =>
      `var(${backgroundColorDefault})`};
    color: ${({ colorDefault }) => `var(${colorDefault})`};

    ${MEDIA.NO_TOUCH} {
      &:hover {
        color: ${({ colorHovered }) => `var(${colorHovered})`};
        box-shadow: ${({ borderWidth, bordersColorHovered }) =>
          `inset 0 0 0 var(${
            BORDERS[borderWidth || ButtonBordersWidth.BORDER_0]
          }) var(${bordersColorHovered})`};

        ${ButtonOverlaySC} {
          background: ${({ backgroundColorHovered }) =>
            `var(${backgroundColorHovered})`};
        }
      }
      &:active {
        color: ${({ colorPressed }) => `var(${colorPressed})`};
        box-shadow: ${({ borderWidth, bordersColorPressed }) =>
          `inset 0 0 0 var(${
            BORDERS[borderWidth || ButtonBordersWidth.BORDER_0]
          }) var(${bordersColorPressed})`};

        ${ButtonOverlaySC} {
          background: ${({ backgroundColorPressed }) =>
            `var(${backgroundColorPressed})`};
        }
      }
    }

    &:disabled {
      cursor: not-allowed;
      background: ${({ backgroundColorDisabled }) =>
        `var(${backgroundColorDisabled})`};
      color: ${({ colorDisabled }) => `var(${colorDisabled})`};
      box-shadow: ${({ borderWidth, bordersColorDisabled }) =>
        `inset 0 0 0 var(${
          BORDERS[borderWidth || ButtonBordersWidth.BORDER_0]
        }) var(${bordersColorDisabled})`};
      &:before {
        opacity: 0;
      }

      ${ButtonOverlaySC} {
        background: ${({ backgroundColorDisabled }) =>
          `var(${backgroundColorDisabled})`};
      }
    }

    &:before {
      content: '';
      height: 100%;
      width: 100%;
      position: absolute;
      left: 0;
      top: 0;
      display: ${({ gradient }) => (gradient ? 'block' : 'none')};
      background: ${({ gradient }) => `var(${gradient})`};
      opacity: 1;
      transition: opacity var(${TRANSITIONS.LINEAR_200});
      z-index: 0;
    }

    ${MEDIA.NO_TOUCH} {
      &:hover:before {
        opacity: 0;
      }
    }

    ${TextBoxSC} {
      color: inherit;
      z-index: 1;
      order: 1;
    }

    ${IconSC} {
      z-index: 1;
      color: inherit;
      order: ${({ iconOrder }) => iconOrder || 1};
      margin: ${({ iconOrder }) => {
        if (iconOrder === 1) {
          return `0 var(${FIXED_INDENTS.INDENT_20}) 0 0`
        }

        if (iconOrder === 2) {
          return `0 0 0 var(${FIXED_INDENTS.INDENT_20})`
        }

        return 'initial'
      }};

      &:only-child {
        margin: 0;
      }
    }
  }
`

export const ButtonContentSC = styled.div<{
  loaderSize: ButtonLoaderSizes
}>`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  width: 100%;
  min-width: 0;
  z-index: 1;

  ${TextBoxSC} {
    display: block;
    width: max-content;
    min-width: max-content;
    max-width: none;
    text-align: center;
    grid-column: 2;
  }
`

export const ButtonLoaderSlotSC = styled.div<{
  loaderSize: ButtonLoaderSizes
}>`
  display: inline-flex;
  width: ${({ loaderSize }) => loaderFootprintMap[loaderSize]}px;
  align-items: center;
  justify-content: flex-start;
  justify-self: end;
  grid-column: 1;
`

export const ButtonCenteredLoaderSC = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`

export const ButtonHiddenContentSC = styled.div`
  visibility: hidden;

  ${TextBoxSC} {
    visibility: hidden;
  }
`
